import React, {useState} from 'react'
import styled from 'styled-components'
import CardBodyContentContainer from '../../../common/styles/CardBodyContentContainer'
import Spacer from '../../../common/Spacer'
import {Card, Table as BootstrapTable} from 'react-bootstrap'
import BookIsbnResponse, {BookLinkType} from './BookIsbnResponse'
import BooksitoutImages from '../../../images/BooksitoutImages'
import AddBookModal from '../add/AddBookModal'
import BookStatusBadge from '../BookAddBadge'
import {useNavigate} from 'react-router-dom'
import {BookStatus} from '../BookType'
import ColSpacer from '../../../common/styles/ColSpacer'
import breakpoints from '../../../config/Breakpoints'
import {Author} from '../../../common/Author'
import utils from '../../../common/utils'
import booksitoutIcon from '../../../config/BooksitoutIcon'

const BookIsbnSection: React.FC<{ book: BookIsbnResponse }> = ({book}) => {
    const navigate = useNavigate()

    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false)
    const addModal = () => {
        setIsAddModalOpen(true)
    }

    return (
        <Card>
            <ClickableOverlay
                clickable={!!book.bookId}
                onClick={() => {
                    book.bookId && navigate(`/book/mine/${book.bookId}`)
                    book.status === 'NOT_ADDED' && addModal()
                }}
            />
            <GlowCard status={book.status}/>
            <BookStatusBadgeContainer>
                <BookStatusBadge bookStatus={book.status} onClick={addModal}/>
            </BookStatusBadgeContainer>

            <AddBookModal
                isAddModalOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                selectedBook={{
                    bookId: book.bookId,
                    isbn13: book.isbn13,
                    title: book.title,
                    subTitle: book.subTitle,
                    authors: book.authors,
                    coverUrl: book.coverUrl,
                    publisher: book.publisher,
                    publishedYear: book.publishedYear,
                    page: book.page,
                    language: book.language,
                    category: book.category,
                    status: book.status,
                    price: book.price,
                    priceCurrency: book.priceCurrency,
                }}
            />

            <CardBodyContentContainer height={400}>
                <Container>
                    <BookCoverContainer>
                        <BookCover src={book.coverUrl ?? BooksitoutImages.Placeholder.bookCover} alt={book.title} onError={(e) => e.target.src = BooksitoutImages.Placeholder.bookCover}/>
                    </BookCoverContainer>

                    <BookInfoContainer>
                        <div className={`d-flex flex-column flex-md-row`}>
                            <BookTitle>{book.title}</BookTitle>

                            {book.originalIsbn13 != null && book.originalTitle != null ? (
                                <>
                                    <ColSpacer size={5}/>

                                    <BookOriginalTitleLink
                                        href={`/book/${book.originalIsbn13}?q=${book.originalTitle}`}>{book.originalTitle}</BookOriginalTitleLink>
                                </>
                            ) : book.originalTitle != null ? (
                                <>
                                    <ColSpacer size={5}/>

                                    <BookOriginalTitle>{book.originalTitle}</BookOriginalTitle>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>

                        <Author heading="h2" size={22} bold>
                            {book.author && book.author.length > 0 ? (
                                [...book.author]
                                    .sort((a, b) => (a.type === 'PRIMARY' ? -1 : b.type === 'PRIMARY' ? 1 : 0))
                                    .map((a, i) => (
                                        <span key={`${a.name}-${i}`}>
                                            {i > 0 && ', '}
                                            {a.urlName ? (
                                                <AuthorLink href={`/book/author/${a.urlName}`} onClick={(e) => e.stopPropagation()}>{a.name}</AuthorLink>
                                            ) : (
                                                a.name
                                            )}
                                            {a.typeDisplayName && <AuthorRole> ({a.typeDisplayName})</AuthorRole>}
                                        </span>
                                    ))
                            ) : (
                                book.authors
                            )}
                        </Author>

                        <Spacer/>

                        <span className={'pt-4 pt-md-0'}/>

                        <Table hover>
                            <tbody>
                            <tr>
                                <InfoTitle>출판사</InfoTitle>

                                <InfoContent className={`${(book.publisher?.urlName || book.publisher?.homePageUrl) && 'clickable'}`}
                                             onClick={(e) => {
                                                 if (book.publisher?.urlName) {
                                                     e.stopPropagation()
                                                     navigate(`/book/publisher/${book.publisher.urlName}`)
                                                 } else if (book.publisher?.homePageUrl) {
                                                     window.open(book.publisher.homePageUrl, '_blank')
                                                 }
                                             }}>
                                    {book.publisher?.logoImageUrl && <Logo src={book.publisher?.logoImageUrl} className={'me-1'} onError={(e) => e.target.style.display = 'none'}/>}
                                    {book.publisher?.name ?? '-'}
                                </InfoContent>
                            </tr>

                            <tr>
                                <InfoTitle>페이지</InfoTitle>
                                <InfoContent>{book.page != null ? `${book.page}쪽` : '-'}</InfoContent>
                            </tr>

                            <tr>
                                <InfoTitle>출판년도</InfoTitle>
                                <InfoContent>{book.publishedYear}년</InfoContent>
                            </tr>
                            </tbody>
                        </Table>

                        <Spacer/>

                        <div
                            className="d-flex flex-column flex-md-row align-items-sm-center gap-2 pt-md-0 pt-4 force-1-line">
                            <div>
                                <BookLink type={'ALADIN'} url={book.externalLinks.aladin}/>
                                <BookLink type={'YES24'} url={book.externalLinks.yes24}/>
                                <BookLink type={'KYOBO'} url={book.externalLinks.kyobo}/>

                                {/* 한국어 책이거나 언어를 모르면 안 보여줌, 근데 amazon link가 어차피 있다면 그냥 보여줌 */}
                                {((book.language != 'KOREAN' && book.language != 'UNKNOWN') || (book.externalLinks.amazon != null && book.externalLinks.amazon != '')) &&
                                    <BookLink type={'AMAZON'} url={book.externalLinks.amazon}/>}

                                {book.externalLinks.naver != null && book.externalLinks.naver != '' &&
                                    <BookLink type={'NAVER'} url={book.externalLinks.naver}/>}
                            </div>

                            <div className={'pt-4 pt-sm-4 align-self-end pe-2'}>
                                {book?.price != null && (
                                    <>
                                        <h6 style={{color: 'var(--sub-text-color)'}}
                                            className="d-flex gap-1 text-nowrap m-0 align-items-center">
                                            {book?.priceCurrency === 'KRW' ? `${utils.insertCommas(book?.price)}원` : book?.priceCurrency === 'USD' ? `$${utils.insertCommas(book?.price)}` : book?.priceCurrency === 'EUR' ? `€${utils.insertCommas(book?.price)}` : book?.priceCurrency === 'JPY' ? `¥${utils.insertCommas(book?.price)}` : utils.insertSpace(book?.price)}
                                            {book?.priceCurrency === 'KRW' && (
                                                <a href="https://namu.wiki/w/도서정가제" target="_blank" rel="noreferrer" style={{ color: 'var(--primary-color)' }}>
                                                    {React.createElement(booksitoutIcon.info, { size: 16 })}
                                                </a>
                                            )}
                                        </h6>
                                    </>
                                )}
                            </div>
                        </div>
                    </BookInfoContainer>
                </Container>
            </CardBodyContentContainer>
        </Card>
    )
}

const Container = styled.div.attrs({
    className: 'row',
})`
    height: 100%;
    padding: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 0;
    }
`

const BookCoverContainer = styled.div.attrs({
    className: 'col-12 col-md-4 col-xl-3 justify-content-center text-center',
})`
    height: 100%;
    align-content: center;
`

const BookInfoContainer = styled.div.attrs({
    className: 'col-12 col-md-8 col-xl-9',
})`
    padding-top: 20px;
    padding-left: 30px;
    padding-right: 5px;
    padding-bottom: 20px;

    display: flex;
    flex-direction: column;

    @media screen and (max-width: ${breakpoints.md}) {
        padding-top: 30px;
        padding-left: 5px;
        text-align: center;
    }
`

const BookCover = styled.img.attrs({
    className: 'img-fluid rounded border',
})`
`

const BookTitle = styled.h1`
    font-size: 2rem;
    font-weight: bold;
    padding-bottom: 5px;

    @media screen and (max-width: ${breakpoints.md}) {
        font-size: 1.5rem;
    }
`

const BookOriginalTitleLink = styled.a.attrs({className: 'text-secondary clamp-1-line'})`
    font-size: 1.2rem;
    font-weight: bold;
    font-style: italic;
    padding-top: 13px;

    @media screen and (max-width: ${breakpoints.md}) {
        padding-top: 0;
        padding-bottom: 13px;
        text-align: center;
    }
`

const BookOriginalTitle = styled.h2.attrs({className: 'text-secondary clamp-1-line'})`
    font-size: 1.2rem;
    font-weight: bold;
    font-style: italic;
    padding-top: 13px;

    @media screen and (max-width: ${breakpoints.md}) {
        padding-top: 0;
        padding-bottom: 13px;
        text-align: center;
        font-size: 1rem;
    }
`

const InfoTitle = styled.td`
    background-color: var(--card-bg-color) !important;
    color: var(--text-color) !important;
    text-align: start;
    justify-content: start;

    @media screen and (max-width: ${breakpoints.md}) {
        text-align: center;
        justify-content: center;
    }
`

const InfoContent = styled.td`
    background-color: var(--card-bg-color) !important;
    color: var(--text-color) !important;
    text-align: start;
    justify-content: start;

    @media screen and (max-width: ${breakpoints.md}) {
        text-align: center;
        justify-content: center;
    }
`

const Table = styled(BootstrapTable)`
    max-width: 500px;

    @media screen and (max-width: ${breakpoints.md}) {
        max-width: 100%;
    }
`

const BookLink: React.FC<{ type: BookLinkType; url: string | null }> = ({type, url}) => {
    const navigate = useNavigate()

    const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation()

        if (!url) {
            e.preventDefault()
            return
        }

        if (e.currentTarget.target !== '_blank') {
            e.preventDefault()
            navigate(url)
        }
    }

    return (
        <LinkATag href={url || undefined} target={url ? '_blank' : undefined} onClick={onClick}>
            {type === 'NAVER' ? <LinkImage src={BooksitoutImages.WebsiteLogo.naver} alt="Naver"/> : type === 'ALADIN' ?
                <LinkImage src={BooksitoutImages.WebsiteLogo.aladin} alt="Aladin"/> : type === 'YES24' ?
                    <LinkImage src={BooksitoutImages.WebsiteLogo.yes24} alt="Yes24"/> : type === 'KYOBO' ?
                        <LinkImage src={BooksitoutImages.WebsiteLogo.kyobo} alt="Kyobo"/> : type === 'AMAZON' ?
                            <LinkImage src={BooksitoutImages.WebsiteLogo.amazon} alt="Amazon"/> : null}
        </LinkATag>
    )
}

const LinkATag = styled.a.attrs<{ href?: string }>(props => ({
    className: 'clickable',
    href: props.href || undefined,
}))`
    ${props =>
            !props.href &&
            `
        pointer-events: none;
        color: grey;
        text-decoration: none;
        opacity: 0.5;
    `}
    
    margin-left: 5px;
    margin-right: 5px;
`

const LinkImage = styled.img.attrs({
    className: 'hover-effect clickable',
})`
    width: 50px;
    height: 50px;
    border-radius: 12px;
`

const Logo = styled.img`
    width: 25px;
    height: 25px;
    border-radius: 6px;
`

const GlowCard = styled.div<{ status: BookStatus }>`
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 100%;
    background: linear-gradient(90deg, ${props => (props.status == 'NOT_ADDED' ? '#9ca3af' : props.status == 'GIVE_UP' ? '#ef4444' : '#22c55e')} 0%, ${props => (props.status == 'NOT_ADDED' ? '#9ca3af' : props.status == 'GIVE_UP' ? '#ef4444' : '#22c55e')} 70%, transparent 100%);
    z-index: 1;
`

const ClickableOverlay = styled.div<{ clickable: boolean }>`
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 100%;
    z-index: 10;
    cursor: pointer;
`

const AuthorLink = styled.a`
    color: inherit;
    text-decoration: none;

    &:hover {
        color: var(--primary-color);
    }
`

const AuthorRole = styled.span`
    font-size: 0.75em;
    color: var(--sub-text-color);
`

const BookStatusBadgeContainer = styled.div`
    position: absolute;
    right: 0;
    top: -15px;
    z-index: 10;
`

export default BookIsbnSection
